import React, { useState, useEffect } from 'react'
import InfoCard from '../../components/shared/InfoCard'
import Modal from '../../components/shared/Modal'
import { formatOptionValue } from '../../utils/formatters'
import { supabase } from '../../lib/supabase'
import { Option } from '../../types/database'

interface OptionsSectionProps {
  modelId: string
}

export default function OptionsSection({ modelId }: OptionsSectionProps) {
  const [options, setOptions] = useState<Option[]>([])
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [addForm, setAddForm] = useState({ name: '', value: '' })
  const [editForm, setEditForm] = useState({ option_id: '', name: '', value: '' })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    fetchOptions()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId])

  const fetchOptions = async () => {
    try {
      const { data, error } = await supabase
        .from('options')
        .select('*')
        .eq('model_id', parseInt(modelId, 10))
        .order('name')

      if (error) throw error
      setOptions(data || [])
    } catch (err: any) {
      // Error fetching options
    }
  }

  const handleAddOption = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!addForm.name || !addForm.value) return

    try {
      setSubmitting(true)
      const { error } = await supabase
        .from('options')
        .insert({
          model_id: parseInt(modelId, 10),
          name: addForm.name,
          value: parseInt(addForm.value, 10)
        })

      if (error) throw error
      await fetchOptions()
      setAddForm({ name: '', value: '' })
      setShowAddModal(false)
    } catch (err: any) {
      alert('Failed to add option: ' + err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleEditOption = (option: Option) => {
    setEditForm({
      option_id: option.option_id,
      name: option.name,
      value: option.value.toString()
    })
    setShowEditModal(true)
  }

  const handleUpdateOption = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editForm.name || !editForm.value) return

    try {
      setSubmitting(true)
      const { error } = await supabase
        .from('options')
        .update({
          name: editForm.name,
          value: parseInt(editForm.value, 10)
        })
        .eq('option_id', editForm.option_id)

      if (error) throw error
      await fetchOptions()
      setEditForm({ option_id: '', name: '', value: '' })
      setShowEditModal(false)
    } catch (err: any) {
      alert('Failed to update option: ' + err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteOption = async (optionId: string) => {
    if (!window.confirm('Are you sure you want to delete this option?')) return

    try {
      const { error } = await supabase
        .from('options')
        .delete()
        .eq('option_id', optionId)

      if (error) throw error
      await fetchOptions()
    } catch (err: any) {
      alert('Failed to delete option: ' + err.message)
    }
  }

  return (
    <>
      <InfoCard
        title="Optional Equipment"
        action={
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-purple-200 text-purple-950 hover:bg-purple-300 px-3 py-1 rounded text-sm font-medium transition-colors duration-150"
          >
            Add Option
          </button>
        }
      >
        <div className="space-y-4">
          {options.length > 0 ? (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Value
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {options.map((option) => (
                    <tr key={option.option_id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                        {option.name}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatOptionValue(option.value)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-3">
                          <button
                            onClick={() => handleEditOption(option)}
                            className="text-purple-600 hover:text-purple-800 transition-colors duration-150"
                            title="Edit option"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteOption(option.option_id)}
                            className="text-red-600 hover:text-red-800 transition-colors duration-150"
                            title="Delete option"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">
              No optional equipment configured yet.
            </p>
          )}
        </div>
      </InfoCard>

      {/* Add Option Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false)
          setAddForm({ name: '', value: '' })
        }}
        title="Add Option"
      >
        <form onSubmit={handleAddOption}>
          <div className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-900 mb-2">
                Option Name
              </label>
              <input
                type="text"
                id="name"
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                placeholder="Synthetic Vision, GoGo WiFi, etc.."
                className="block w-full px-3 py-2.5 text-gray-900 border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label htmlFor="value" className="block text-sm font-medium text-gray-900 mb-2">
                Value
              </label>
              <input
                type="number"
                id="value"
                value={addForm.value}
                onChange={(e) => setAddForm({ ...addForm, value: e.target.value })}
                placeholder="10000000"
                className="block w-full px-3 py-2.5 text-gray-900 border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                required
              />
              <p className="mt-2 text-xs text-gray-500">
                Enter the value without any formatting (commas, dollar signs, etc.) - formatting will be applied automatically.
              </p>
            </div>
          </div>
          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowAddModal(false)
                setAddForm({ name: '', value: '' })
              }}
              className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent bg-purple-950 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-purple-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Adding...' : 'Add Option'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Option Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setEditForm({ option_id: '', name: '', value: '' })
        }}
        title="Edit Option"
      >
        <form onSubmit={handleUpdateOption}>
          <div className="space-y-5">
            <div>
              <label htmlFor="edit-name" className="block text-sm font-medium text-gray-900 mb-2">
                Option Name
              </label>
              <input
                type="text"
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Synthetic Vision, GoGo WiFi, etc.."
                className="block w-full px-3 py-2.5 text-gray-900 border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label htmlFor="edit-value" className="block text-sm font-medium text-gray-900 mb-2">
                Value
              </label>
              <input
                type="number"
                id="edit-value"
                value={editForm.value}
                onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                placeholder="10000000"
                className="block w-full px-3 py-2.5 text-gray-900 border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                required
              />
              <p className="mt-2 text-xs text-gray-500">
                Enter the value without any formatting (commas, dollar signs, etc.) - formatting will be applied automatically.
              </p>
            </div>
          </div>
          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowEditModal(false)
                setEditForm({ option_id: '', name: '', value: '' })
              }}
              className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent bg-purple-950 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-purple-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Updating...' : 'Update Option'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  )
}
